import React from 'react';
import classNames from 'classnames';
import { Control, useController } from 'react-hook-form';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import styles from './SegmentedControl.module.scss';

export type SegmentedValue = string | number | boolean;

export interface SegmentedOption<T extends SegmentedValue> {
  value: T;
  label: string;
  icon?: IconName;
  /** Renders the active state in error colors (e.g. a "locked" option). */
  danger?: boolean;
}

export interface SegmentedControlProps<T extends SegmentedValue> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

export function SegmentedControl<T extends SegmentedValue>({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={classNames(styles.root, { [styles.disabled]: disabled })}>
      {options.map(option => {
        const isActive = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={disabled}
            onClick={() => {
              if (!isActive) {
                onChange(option.value);
              }
            }}
            className={classNames(styles.option, isActive ? 'typo-text-14--bold' : 'typo-text-14', {
              [styles.active]: isActive,
              [styles.danger]: option.danger,
            })}>
            {option.icon && <Icon name={option.icon} width={14} height={14} />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export interface ControlledSegmentedControlProps<T extends SegmentedValue>
  extends Omit<SegmentedControlProps<T>, 'value' | 'onChange'> {
  name: string;
  control: Control<any>;
}

export function ControlledSegmentedControl<T extends SegmentedValue>({
  name,
  control,
  ...rest
}: ControlledSegmentedControlProps<T>) {
  const { field } = useController({ name, control });
  return <SegmentedControl {...rest} value={field.value} onChange={field.onChange} />;
}
